import React from 'react'
import { useFormContext } from 'react-hook-form'
import get from 'lodash/get'
import { FaTimes } from 'react-icons/fa'
import { NumericFormat } from 'react-number-format'

const RequiredError = ({ name, errors }) => {
    if (errors && name) {
        const error = get(errors, name)
        if (error) {
            if (error.type === 'isInteger') {
                return (
                    <span className='has-text-danger'>
                        This field must be an integer
                    </span>
                )
            }
            if (error.type === 'required') {
                return (
                    <span className='has-text-danger'>
                        {error.message ? error.message : 'Required field'}
                    </span>
                )
            }
            return (
                <span className='has-text-danger'>
                    {error.message}
                </span>
            )
        }
    }
    return null
}

const InputFieldHorizontal = ({ label, name, children, hidden = false }) => {
    const { errors } = useFormContext()
    return (
        <div
            className='field is-horizontal'
            style={hidden ? { display: 'none' } : {}}
        >
            <label className='field-label is-normal label'>{label}</label>
            <div className='field-body is-expanded'>
                <div className='field'>
                    <div className='control'>{children}</div>
                    <RequiredError name={name} errors={errors} />
                </div>
            </div>
        </div>
    )
}

const InputFieldSimple = ({ label, name, viewOnly, inputProps }) => {
    const { errors, setValue, clearError } = useFormContext()
    return (
        <div className='field is-horizontal'>
            <label className='field-label is-normal label'>{label}</label>
            <div className='field-body is-expanded'>
                <div className='field'>
                    <div className='control is-expanded has-icons-right'>
                        <input {...inputProps} />
                        {!viewOnly && (
                            <span
                                className='icon is-right'
                                onClick={() => {
                                    setValue(name, null)
                                    clearError(name)
                                }}
                            >
                                <FaTimes />
                            </span>
                        )}
                    </div>
                    {errors && <RequiredError name={name} errors={errors} />}
                </div>
            </div>
        </div>
    )
}

const InputFieldColumnSimple = ({
    label,
    name,
    viewOnly,
    style,
    fieldErrors,
    inputProps,
    onClear 
}) => {
    const baseProps = {
        name,
        disabled: viewOnly,
        className: `input is-normal ${inputProps.className}`,
    }
    return (
        <InputFieldColumn
            label={label}
            name={name}
            viewOnly={viewOnly}
            style={style}
            fieldErrors={fieldErrors}
            onClear={onClear}
        >
            {inputProps.type === 'number' ? (
                <NumericFormat {...baseProps} {...inputProps} />
            ) : (
                <input {...baseProps} {...inputProps} />
            )}
        </InputFieldColumn>
    )
}

const InputFieldColumnSelect = ({
    label,
    name,
    viewOnly,
    options,
    placeholder = `Select...`,
    inputProps,
}) => {
    const { errors } = useFormContext()
    return (
        <div className='field'>
            {label && <label className='label'>{label}</label>}
            <div className='control is-expanded has-icons-right'>
                <InputFieldColumn label={label} name={name}>
                    <div className='select is-normal is-fullwidth'>
                        <select name={name} {...inputProps} disabled={viewOnly}>
                            <option value={''}>{placeholder}</option>
                            {options}
                        </select>
                    </div>
                </InputFieldColumn>
            </div>
            <RequiredError name={name} errors={errors} />
        </div>
    )
}

const InputFieldColumn = ({
    label,
    name,
    style,
    viewOnly,
    fieldErrors,
    isClearable=true,
    children,
    onClear,
}) => {
    const { errors, setValue, clearError } = useFormContext()

    const handleClick = () => {
        if (onClear) {
            onClear()
        } else {
            setValue(name, null)
            clearError(name)
        }
    }

    return (
        <div style={style}>
            {label && <label className='label'>{label}</label>}
            <div className={`control ${!viewOnly && 'has-icons-right'}`}>
                {children}
                {!viewOnly && isClearable &&(
                    <span
                        className='icon is-right'
                        onClick={handleClick} 
                        style={{ cursor: 'pointer' }}>
                        <FaTimes />
                    </span>
                )}
            </div>
            <RequiredError name={name} errors={{ ...errors, ...fieldErrors }} />
        </div>
    )
}

export {
    InputFieldColumn,
    InputFieldHorizontal,
    InputFieldSimple,
    InputFieldColumnSimple,
    InputFieldColumnSelect,
    RequiredError,
}
export default InputFieldHorizontal
